// Parent class responsible for data fetching
import * as L from 'leaflet';
import axios, { CancelTokenSource } from 'axios';
import {
  LeafletExtensionNIBaseLayer,
  NiLayerOptions,
} from './NIBaseClass/LeafletExtensionNIBaseLayer';
import { AlertState, FilterData, GlobalDateFilter } from 'types/types';
import {
  MapDataRequest,
  MapGeoJsonDataResult,
} from 'types/types';
import { showAlert } from 'features/applicationState';
import DataProviderService from 'services/DataProviderService';

export class LeafletExtensionGlobalFilterBasedLayer extends LeafletExtensionNIBaseLayer {
  protected layerDataFromServer?: MapGeoJsonDataResult;
  protected layerDataRequestTokenSource?: CancelTokenSource;
  protected internalLayer: L.FeatureGroup<any>;
  
  constructor(options: NiLayerOptions) {
    super(options);
    this.internalLayer = new L.FeatureGroup(undefined, {});
  }

  public baseMarkersLoadedOrRefreshed(newBaseMarkers: any[]): void {
    super.baseMarkersLoadedOrRefreshed(newBaseMarkers);
    this.internalLayer.clearLayers();
    this.renderLayerData();
  }

  protected renderLayerData() {
    console.warn('renderLayerData not implemented');
  }

  public async filtersChanged(
    newFiltersState: FilterData[],
    newGlobalDateFilter: GlobalDateFilter
  ): Promise<void> {
    let currentRegionFilter = this.currentFiltersState.find(
      (filter) => filter.category === 'regionName'
    );
    let newRegionFilter = newFiltersState.find(
      (filter) => filter.category === 'regionName'
    );
    let needsDataRefresh =
      currentRegionFilter?.selectedValues?.[0]?.id !==
        newRegionFilter?.selectedValues?.[0]?.id ||
      this.currentGlobalDateFilter.values?.[0].id !==
        newGlobalDateFilter.values?.[0].id;

    await super.filtersChanged(newFiltersState, newGlobalDateFilter);

    if (needsDataRefresh) await this._fetchLayerData();
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);

    // start data fetching
    this._fetchLayerData();

    return this;
  }

  onRemove(map: L.Map): this {
    if (this.internalLayer) {
      map.removeLayer(this.internalLayer);
    }
    // if there was a request ongoing, cancel it
    this.layerDataRequestTokenSource?.cancel(
      `Cancelling API request for Layer [${
        this.niLayerOptions.layerDefinition.LayerId ?? ''
      }] due to layer REMOVE`
    );
    return super.onRemove(map);
  }

  _handleLayerOutOfZoom(): void {
    if (this._map && this._map.hasLayer(this.internalLayer)) {
      this._map.removeLayer(this.internalLayer);
    }
  }

  _handleLayerWithinZoomBoundaries(): void {
    if (this._map && !this._map.hasLayer(this.internalLayer)) {
      this.internalLayer.addTo(this._map);
    }
  }


  protected async _fetchLayerData(): Promise<void> {
    try {
      this.setNewLoadingState(true);
      this.internalLayer.clearLayers();
      this.layerDataFromServer = undefined;
      let regionFilter = this.currentFiltersState.find(
        (filter) => filter.category === 'regionName'
      );
      this.layerDataRequestTokenSource?.cancel(
        `Cancelling API request for Layer [${
          this.niLayerOptions.layerDefinition.LayerId ?? ''
        }]`
      );
      this.layerDataRequestTokenSource = axios.CancelToken.source();
      let requestParams: MapDataRequest = {
        podId: 'ni-ran-advanced-map-dashboard-global-layer',
        dashboardId: 'ni-ran-advanced-map-dashboard',
        layerIds: [this.niLayerOptions.layerDefinition.LayerId ?? ''],
        dataFilters: [
          this.currentGlobalDateFilter,
          {
            filterId: 'global_region_filter',
            values: [
              {
                id: regionFilter?.selectedValues?.[0]?.id ?? '',
                name: regionFilter?.selectedValues?.[0]?.name ?? '',
              },
            ],
          },
        ],
      };

      
      const dpService = new DataProviderService();
      const layerData = await dpService.getMapGeoJsonData(requestParams, {
        cancelToken: this.layerDataRequestTokenSource?.token,
      });

      if(layerData?.data?.features?.length > 0){
        this.layerDataFromServer = layerData;
        this.renderLayerData();
      }else{
        const alertData: AlertState = {
          severity: 'warning',
          title: 'No Data',
          message:
            'No data found for "' +
            this.niLayerOptions.layerDefinition.LayerId +
            '" layer the selected filters and date range.',
          open: true,
        };
        this.niLayerOptions.reduxDispatchCallback(showAlert(alertData));
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        let errorMsg =
          'Error while retrieving "' +
          this.niLayerOptions.layerDefinition.LayerId +
          '" layer\'s data.';
        console.error(errorMsg, e);
        const alertData: AlertState = {
          severity: 'error',
          title: 'Data error',
          message: errorMsg,
          open: true,
        };
        this.niLayerOptions.reduxDispatchCallback(showAlert(alertData));
      }
    } finally {
      this.setNewLoadingState(false);
    }
  }
}